import { Menu, UserRound, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useLocale } from '../../features/i18n/LocaleProvider';
import { useAuth } from '../../features/auth/AuthProvider';
import { PwaInstall } from '../../features/pwa/PwaInstall';
import styles from '../../styles/site.module.css';

export function SiteLayout() {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { locale, setLocale, t } = useLocale();
  const { user, isManager } = useAuth();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const navItems = [
    ['/', t('home')],
    ['/about', t('about')],
    ['/lessons', t('lessons')],
    ['/quiz', t('quizzes')],
    ['/blog', t('blog')],
    ['/contact', t('contact')],
  ] as const;

  return (
    <div className={styles.siteShell}>
      <header className={styles.header}>
        <Link className={styles.brand} to="/" aria-label="Сулеймен Аскаров — главная">
          <span className={styles.brandMark}>SA</span>
          <span>
            Сулеймен
            <br />
            Аскаров
          </span>
        </Link>

        <nav className={styles.desktopNav} aria-label="Основная навигация">
          {navItems.map(([to, label]) => (
            <NavLink
              key={to}
              className={({ isActive }) => (isActive ? styles.navActive : undefined)}
              to={to}
              end={to === '/'}
            >
              {label}
            </NavLink>
          ))}
        </nav>

        <div className={styles.headerActions}>
          <Link className={styles.accountLink} to={isManager ? '/dashboard' : user ? '/account' : '/login'} aria-label={isManager ? 'Панель администратора' : user ? 'Личный кабинет' : 'Войти'}>
            <UserRound aria-hidden="true" />
            <span>{isManager ? 'Dashboard' : user ? (locale === 'ru' ? 'Кабинет' : 'Кабинет') : (locale === 'ru' ? 'Войти' : 'Кіру')}</span>
          </Link>
          <div className={styles.localeSwitch} aria-label={t('language')}>
            <button
              className={locale === 'ru' ? styles.localeActive : undefined}
              type="button"
              onClick={() => setLocale('ru')}
            >
              RU
            </button>
            <span>/</span>
            <button
              className={locale === 'kk' ? styles.localeActive : undefined}
              type="button"
              onClick={() => setLocale('kk')}
            >
              KZ
            </button>
          </div>
          <button
            className={styles.menuButton}
            type="button"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? t('close') : t('menu')}
            onClick={() => setMenuOpen((value) => !value)}
          >
            {menuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </header>

      <div className={`${styles.mobileMenu} ${menuOpen ? styles.mobileMenuOpen : ''}`}>
        <nav aria-label="Мобильная навигация">
          {navItems.map(([to, label], index) => (
            <NavLink key={to} to={to}>
              <span>0{index + 1}</span>
              {label}
            </NavLink>
          ))}
          <NavLink to={isManager ? '/dashboard' : user ? '/account' : '/login'}>
            <span>07</span>
            {isManager ? (locale === 'ru' ? 'Админ-панель' : 'Әкімші панелі') : user ? (locale === 'ru' ? 'Личный кабинет' : 'Жеке кабинет') : (locale === 'ru' ? 'Войти' : 'Кіру')}
          </NavLink>
        </nav>
      </div>

      <main>
        <Outlet />
      </main>

      <footer className={styles.footer}>
        <div>
          <p className={styles.footerTitle}>Знание, которое ведёт к ясности.</p>
          <p>Білім — айқындыққа бастайтын жол.</p>
        </div>
        <div className={styles.footerLinks}>
          <a href="#" aria-label="YouTube">
            YouTube
          </a>
          <a href="#" aria-label="Instagram">
            Instagram
          </a>
          <a href="#" aria-label="Telegram">
            Telegram
          </a>
        </div>
        <div className={styles.footerBottom}>
          <span>© {new Date().getFullYear()} Сулеймен Аскаров</span>
          <Link to="/contact">Политика конфиденциальности</Link>
          <Link to="/dashboard">Вход для редактора</Link>
        </div>
      </footer>
      {!menuOpen && <PwaInstall />}
    </div>
  );
}
